import React from "react";
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { People } from "@mui/icons-material";
import type { StudyMember } from "../../../types";
import {
  researcherNameSx,
  researcherTableContainerSx,
  researcherTableHeaderSx,
  researcherTableSx,
} from "../researcherPresentation";

const columns = [
  { label: "Name", align: "left", width: "20%" },
  { label: "Email", align: "left", width: "25%" },
  { label: "Status", align: "center", width: "15%" },
  { label: "Joined Date", align: "center", width: "15%" },
  { label: "Role", align: "center", width: "10%" },
] as const;

function MemberTable({ members }: { members: StudyMember[] }) {
  return (
    <TableContainer sx={researcherTableContainerSx}>
      <Table sx={researcherTableSx}>
        <TableHead>
          <TableRow>
            {columns.map(({ label, align, width }) => (
              <TableCell
                key={label}
                sx={{ ...researcherTableHeaderSx, textAlign: align, width }}
              >
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {members.map((member) => (
            <TableRow key={member.id}>
              <TableCell sx={{ py: 1.5, textAlign: "left" }}>
                <Typography variant="body2" sx={researcherNameSx} title={member.name}>
                  {member.name}
                </Typography>
              </TableCell>
              <TableCell sx={{ py: 1.5, textAlign: "left" }}>
                <Typography variant="body2" color="text.secondary" title={member.email}>
                  {member.email}
                </Typography>
              </TableCell>
              <TableCell sx={{ py: 1.5, textAlign: "center" }}>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Chip
                    variant="outlined"
                    label={member.status}
                    color={member.status === "active" ? "success" : "warning"}
                    size="small"
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ py: 1.5, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {member.joinedDate ? new Date(member.joinedDate).toLocaleDateString() : "N/A"}
                </Typography>
              </TableCell>
              <TableCell sx={{ py: 1.5, textAlign: "center" }}>
                <Chip
                  variant="outlined"
                  label={member.role === "main" ? "Main" : "Collaborator"}
                  color={member.role === "main" ? "primary" : "info"}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function StudyMembersTab({ members }: { members: StudyMember[] }) {
  const mainResearchers = members.filter((member) => member.role === "main");
  const collaborators = members.filter((member) => member.role === "collab");

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          All researchers involved in this study
        </Typography>
      </Box>
      {members.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <People sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>No Study Members</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Study members will appear here once collaborators join the study.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: "primary.main", fontWeight: 600 }}>
              Main Researcher
            </Typography>
            <MemberTable members={mainResearchers} />
          </Box>
          {collaborators.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, color: "info.main", fontWeight: 600 }}>
                Collaborative Researchers ({collaborators.length})
              </Typography>
              <MemberTable members={collaborators} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
